import type { MapIcon as MapIconType } from '@/lib/api';
import { getMapIcon } from '@/lib/api';

/**
 * Singleton cache pro iconData napříč komponentami
 * Sdílí data mezi MapCanvas, MapIconEditor a dalšími komponentami
 */
class IconDataCache {
  private cache = new Map<string, MapIconType>();
  private pendingRequests = new Map<string, Promise<MapIconType | null>>();

  /**
   * Získá iconData z cache nebo načte z API
   * @param iconId - ID ikony ze Supabase
   * @returns Promise s iconData nebo null pokud se nepodařilo načíst
   */
  async get(iconId: string): Promise<MapIconType | null> {
    // Pokud je v cache, vrátit přímo
    if (this.cache.has(iconId)) {
      return this.cache.get(iconId)!;
    }

    // Pokud již probíhá načítání této ikony, vrátit existující Promise
    if (this.pendingRequests.has(iconId)) {
      return this.pendingRequests.get(iconId)!;
    }

    // Vytvořit nový Promise pro načtení
    const loadPromise = this.loadIconData(iconId);
    this.pendingRequests.set(iconId, loadPromise);

    try {
      const iconData = await loadPromise;
      return iconData;
    } finally {
      // Vyčistit pending request po dokončení
      this.pendingRequests.delete(iconId);
    }
  }

  /**
   * Načte iconData z API a uloží do cache
   */
  private async loadIconData(iconId: string): Promise<MapIconType | null> {
    try {
      const result = await getMapIcon(iconId);
      
      if (result.success && result.data) {
        // Uložit do cache
        this.cache.set(iconId, result.data);
        return result.data;
      }
      
      return null;
    } catch (error) {
      console.warn(`Failed to load icon data for ${iconId}:`, error);
      return null;
    }
  }

  /**
   * Vyčistí cache
   */
  clear(): void {
    this.cache.clear();
    this.pendingRequests.clear();
  }

  /**
   * Smaže konkrétní položku z cache
   */
  delete(iconId: string): void {
    this.cache.delete(iconId);
    this.pendingRequests.delete(iconId);
  }

  /**
   * Vrátí počet položek v cache
   */
  getCacheSize(): number {
    return this.cache.size;
  }

  /**
   * Vrátí všechny ID ikon v cache
   */
  getCachedIds(): string[] {
    return Array.from(this.cache.keys());
  }
}

// Export singleton instance
export const iconDataCache = new IconDataCache();












